import React, { useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const BAND_TONE = { High: undefined, Medium: "warn", Low: "danger", Unknown: "muted" };

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function renderValue(value) {
  if (value === null || value === undefined || value === "") return <em style={{ color: "var(--bo-muted)" }}>—</em>;
  if (Array.isArray(value)) {
    return (
      <ul style={{ margin: "6px 0", paddingLeft: 20 }}>
        {value.map((v, i) => <li key={`${v}-${i}`}>{String(v)}</li>)}
      </ul>
    );
  }
  return <div>{String(value)}</div>;
}

function truncate(s, n = 400) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function ProposalCard({ proposal, onAction }) {
  const [busy, setBusy] = useState(null); // null | 'accept' | 'reject' | 'defer' | 'canonical'
  const [error, setError] = useState(null);
  const [showSource, setShowSource] = useState(false);
  const band = proposal.confidence_band || "Unknown";
  const factors = proposal.confidence_factors || {};

  const doAction = async (action) => {
    setBusy(action);
    setError(null);
    try {
      await onAction(proposal.id, action);
    } catch (e) {
      setError(formatApiError(e));
      setBusy(null);
    }
  };

  return (
    <div className="bo-card" style={{ padding: 18, marginBottom: 12 }} data-testid={`proposal-card-${proposal.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: "var(--bo-mono)", fontSize: 12, color: "var(--bo-muted)" }}>
            {proposal.target_field_path}
          </div>
          <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 2 }}>
            {proposal.scope} · created {fmtDate(proposal.created_at)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Pill tone={BAND_TONE[band]} testId={`proposal-band-${proposal.id}`}>{band}</Pill>
          <Pill tone="muted">{factors.authority || "—"}</Pill>
          <Pill tone="muted">{factors.source_type || "—"}</Pill>
        </div>
      </div>

      <div
        style={{
          marginTop: 10, padding: 12,
          background: "var(--bo-panel-alt)", border: "1px solid var(--bo-line)",
          borderRadius: "var(--bo-radius-sm)",
        }}
        data-testid={`proposal-value-${proposal.id}`}
      >
        {renderValue(proposal.proposed_value)}
      </div>

      {proposal.review_note && (
        <p style={{ marginTop: 10, fontSize: 13, color: "var(--bo-muted)", fontStyle: "italic" }} data-testid={`proposal-rationale-${proposal.id}`}>
          Model rationale: {proposal.review_note}
        </p>
      )}

      {proposal.source_title && (
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            className="bo-btn bo-btn-ghost"
            style={{ padding: "4px 10px", fontSize: 12 }}
            onClick={() => setShowSource((s) => !s)}
            data-testid={`proposal-source-toggle-${proposal.id}`}
          >
            {showSource ? "Hide source" : `Source: ${truncate(proposal.source_title, 60)}`}
          </button>
          {showSource && (
            <pre
              style={{
                marginTop: 8, padding: 10,
                background: "var(--bo-panel-alt)", border: "1px solid var(--bo-line)",
                borderRadius: "var(--bo-radius-sm)",
                whiteSpace: "pre-wrap", fontSize: 12, color: "var(--bo-ink-soft)",
                fontFamily: "var(--bo-mono)",
              }}
              data-testid={`proposal-source-excerpt-${proposal.id}`}
            >
              {truncate(proposal.source_chunk_excerpt, 700)}
            </pre>
          )}
        </div>
      )}

      {error && <div className="bo-login-error" role="alert" data-testid={`proposal-error-${proposal.id}`}>{error}</div>}

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          onClick={() => doAction("accept")}
          disabled={!!busy}
          data-testid={`proposal-accept-${proposal.id}`}
        >
          {busy === "accept" ? "Accepting…" : "Accept"}
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-secondary"
          onClick={() => doAction("canonical")}
          disabled={!!busy}
          data-testid={`proposal-canonical-${proposal.id}`}
          title="Promote as canonical (highest authority)"
        >
          {busy === "canonical" ? "Promoting…" : "Mark canonical"}
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-secondary"
          onClick={() => doAction("defer")}
          disabled={!!busy}
          data-testid={`proposal-defer-${proposal.id}`}
        >
          Defer
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          onClick={() => doAction("reject")}
          disabled={!!busy}
          data-testid={`proposal-reject-${proposal.id}`}
        >
          Reject
        </button>
      </div>
    </div>
  );
}

function ExtractionKickoffCard({ onExtract }) {
  const [chunks, setChunks] = useState([]);
  const [selectedChunk, setSelectedChunk] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [force, setForce] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/knowledge/chunks?limit=30");
        setChunks(res.data.chunks || []);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const start = async () => {
    if (!selectedChunk) { setError("Select a chunk to extract."); return; }
    setError(null); setRunning(true); setJobStatus("Queued…");
    try {
      const jobRes = await api.post("/intelligence/jobs", {
        kind: "extract",
        payload: { chunk_id: selectedChunk, force },
      });
      const jobId = jobRes.data.job.id;
      setJobStatus("Running…");
      // Poll
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const p = await api.get(`/intelligence/jobs/${jobId}`);
        const job = p.data.job;
        if (job.status === "succeeded" || job.status === "failed" || job.status === "paused") {
          setJobStatus(`${job.status} · $${(job.total_cost_usd || 0).toFixed(4)} · ${job.result?.proposals_added ?? 0} proposal${job.result?.proposals_added === 1 ? "" : "s"}${job.result?.skipped ? " (cache hit)" : ""}`);
          onExtract();
          break;
        }
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bo-card" style={{ marginBottom: 16 }} data-testid="rq-extract-card">
      <div className="bo-eyebrow">Run extraction</div>
      <h2 className="bo-card-title">Extract Brand Brain from a source chunk</h2>
      <p className="bo-card-lede">
        Runs Claude Sonnet 4.5 over one chunk and drops candidate proposals here.
        Each extraction is cached by SHA-256 so re-running the same chunk costs $0.
      </p>

      {loading && <LoadingCard label="Loading chunks…" />}
      {error && <ErrorBanner error={error} />}

      {!loading && chunks.length === 0 && (
        <EmptyState title="No knowledge chunks available" description="Add knowledge sources first (Gate 1)." />
      )}

      {!loading && chunks.length > 0 && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 10, alignItems: "end", marginTop: 10 }}>
            <label>
              <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 4 }}>Chunk</div>
              <select
                className="bo-input"
                value={selectedChunk}
                onChange={(e) => setSelectedChunk(e.target.value)}
                data-testid="rq-chunk-select"
              >
                <option value="">— pick a chunk —</option>
                {chunks.map((c) => (
                  <option key={c.id} value={c.id}>
                    {truncate(c.source_title || c.text || c.id, 80)}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, paddingBottom: 8 }}>
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                data-testid="rq-force-checkbox"
              />
              <span style={{ fontSize: 13 }}>Force (blog/external)</span>
            </label>
            <button
              type="button"
              className="bo-btn bo-btn-primary"
              onClick={start}
              disabled={running}
              data-testid="rq-extract-btn"
            >
              {running ? "Extracting…" : "Extract"}
            </button>
          </div>
          {jobStatus && (
            <p style={{ marginTop: 10, fontSize: 13, color: "var(--bo-muted)" }} data-testid="rq-job-status">
              Job: {jobStatus}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default function ReviewQueue() {
  const [proposals, setProposals] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const res = await api.get("/intelligence/proposals?status=pending&limit=100");
      setProposals(res.data.proposals || []);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const actOn = async (proposalId, action) => {
    const paths = {
      accept: "accept",
      canonical: "canonical",
      defer: "defer",
      reject: "reject",
    };
    const path = paths[action];
    if (!path) throw new Error(`Unknown action: ${action}`);
    await api.post(`/intelligence/proposals/${proposalId}/${path}`, {
      review_note: null,
    });
    // Remove locally for snappy UX; also refresh in background.
    setProposals((cur) => (cur || []).filter((p) => p.id !== proposalId));
    // Debounced refresh
    setTimeout(load, 400);
  };

  const grouped = useMemo(() => {
    if (!proposals) return {};
    const out = {};
    for (const p of proposals) {
      const domain = (p.target_field_path || "").split(".")[0] || "other";
      if (!out[domain]) out[domain] = [];
      out[domain].push(p);
    }
    return out;
  }, [proposals]);

  return (
    <Shell title="Review Queue">
      <div className="bo-eyebrow" data-testid="rq-eyebrow">Gate 2 · Slice 2B · Intelligence Review</div>
      <h1
        className="bo-serif"
        style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }}
        data-testid="rq-title"
      >
        Review Queue
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 720, margin: "0 0 20px 0" }} data-testid="rq-lede">
        Candidate Brand Brain statements extracted from your knowledge sources.
        Nothing is auto-approved — every proposal waits here for your Accept,
        Correct, Reject, Defer, or Mark Canonical action.
      </p>

      <ExtractionKickoffCard onExtract={load} />

      <ErrorBanner error={error} />
      {proposals === null && <LoadingCard label="Loading proposals…" />}

      {proposals !== null && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
            <p style={{ margin: 0, color: "var(--bo-muted)" }} data-testid="rq-count">
              {proposals.length} pending proposal{proposals.length === 1 ? "" : "s"}
            </p>
            <button
              type="button"
              className="bo-btn bo-btn-secondary"
              onClick={load}
              disabled={refreshing}
              data-testid="rq-refresh-btn"
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {proposals.length === 0 ? (
            <EmptyState
              title="No pending proposals"
              description="Run an extraction above, or wait for Slice 2B batch extractions to populate this queue."
              testId="rq-empty"
            />
          ) : (
            Object.entries(grouped).map(([domain, ps]) => (
              <section key={domain} style={{ marginBottom: 20 }} data-testid={`rq-domain-${domain}`}>
                <h2 className="bo-serif" style={{ fontSize: 20, margin: "0 0 10px 0", textTransform: "capitalize" }}>
                  {domain.replace(/_/g, " ")} <span style={{ color: "var(--bo-muted)", fontSize: 14, fontWeight: 400 }}>({ps.length})</span>
                </h2>
                {ps.map((p) => (
                  <ProposalCard key={p.id} proposal={p} onAction={actOn} />
                ))}
              </section>
            ))
          )}
        </>
      )}
    </Shell>
  );
}
